import React, { useContext } from "react";
import { AppContext } from "../../context/AppContext";
import { PartnerFile } from "../../services/PartnerFileService";
import { formatBytes } from "../../utils/format";
import {
  FileQuota,
  FileTable,
  formatPhrase,
  getLocale,
  groupByFolder
} from "./shared";

/**
 * one release of the partner's own files with the customer behind it already
 * resolved to a name
 */
export type ProvidedGroupView = {
  key: string;
  visibility: "public" | "private";
  customerId: string | null;
  customerName: string;
  files: PartnerFile[];
};

/**
 * ProvidedFilesProps
 */
type ProvidedFilesProps = {
  quota: FileQuota;
  groups: ProvidedGroupView[];
  pendingFileId: string;
  onDownload: (file: PartnerFile) => void;
};

/**
 * ProvidedFiles
 * what the logged in partner offers as a supplier: its storage consumption, and
 * below it its own files -- grouped by who may see them and, inside a group, by
 * the folder they sit in, because that is the structure it uploaded them in
 * @param props
 * @constructor
 */
export const ProvidedFiles: React.FC<ProvidedFilesProps> = ({
  quota,
  groups,
  pendingFileId,
  onDownload
}) => {
  const { I18N } = useContext(AppContext);

  const locale: string = getLocale(I18N),
    /**
     * groups without a single file are left out entirely: their heading would
     * announce a release that has nothing in it
     */
    filledGroups: ProvidedGroupView[] = groups.filter((group) => !!group.files.length),
    /**
     * a partner can provide files without a subscription that grants storage.
     * then there is no share to show, only the storage it occupies
     */
    hasLimit: boolean = quota.max > 0,
    /**
     * quotaPercent
     */
    quotaPercent: number = hasLimit
      ? Math.min(100, Math.max(0, Math.round((quota.used / quota.max) * 100)))
      : 0;

  /**
   * render
   */
  return (
    <div data-file-exchange-provided>
      {/* the storage belongs to the partner as a supplier, so it is shown here
          and only here */}
      <div data-file-exchange-quota>
        <h2 data-file-exchange-quota-title>{I18N.t("Mein Speicherplatz")}</h2>

        {hasLimit ? (
          <>
            <p data-file-exchange-quota-text>
              {formatPhrase(I18N.t("%s von %s belegt"), [
                formatBytes(quota.used, locale),
                formatBytes(quota.max, locale)
              ])}
            </p>

            <div
              data-file-exchange-quota-bar
              role="progressbar"
              aria-label={I18N.t("Belegter Speicherplatz")}
              aria-valuenow={quotaPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span
                data-file-exchange-quota-bar-fill
                style={{ width: quotaPercent + "%" }}
              />
            </div>
          </>
        ) : (
          <>
            {/* without a booked limit the occupied storage is still worth
                showing, but there is no share to fill a bar with -- and the
                missing limit is said out loud instead of left to guessing */}
            <p data-file-exchange-quota-text>
              {formatPhrase(I18N.t("%s belegt"), [formatBytes(quota.used, locale)])}
            </p>

            <p data-file-exchange-quota-note>
              {I18N.t("Für Sie ist derzeit kein Speicherkontingent gebucht.")}
            </p>
          </>
        )}
      </div>

      {!filledGroups.length ? (
        /* storage, but nothing in it: the upload does not happen in this mask,
           so this is the one place to say where it does happen */
        <p data-file-exchange-provided-empty>
          {I18N.t("Sie stellen derzeit keine Dateien bereit. Laden Sie Ihre Dateien per SFTP hoch, sie erscheinen hier mit wenigen Minuten Verzögerung.")}
        </p>
      ) : (
        <>
          {filledGroups.map((group) => {
            const heading = group.visibility === "private"
                ? formatPhrase(I18N.t("Nur für %s"), [group.customerName])
                : I18N.t("Für alle meine Kunden"),
              folders = groupByFolder(group.files, locale);

            return (
              <div
                key={group.key}
                data-file-exchange-provided-group
              >
                <h2 data-file-exchange-visibility-heading>{heading}</h2>

                {folders.map((folder) => {
                  const folderLabel = folder.path || I18N.t("Hauptverzeichnis"),
                    count = folder.files.length;

                  return (
                    <div
                      key={folder.path || "/"}
                      data-file-exchange-folder-group
                    >
                      <h3 data-file-exchange-folder-heading>
                        {folderLabel}
                        <span data-file-exchange-folder-count>
                          {count === 1
                            ? I18N.t("1 Datei")
                            : formatPhrase(I18N.t("%s Dateien"), [count])}
                        </span>
                      </h3>

                      <FileTable
                        files={folder.files}
                        /* the release is named as well: the root folder of two
                           releases would otherwise carry the same caption */
                        label={formatPhrase(
                          I18N.t("Dateien in %s (%s)"),
                          [folderLabel, heading]
                        )}
                        variant="provided"
                        pendingFileId={pendingFileId}
                        onDownload={onDownload}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* same five minute server cache as on the customer side */}
          <p data-file-exchange-hint>
            {I18N.t("Neu hochgeladene Dateien erscheinen mit wenigen Minuten Verzögerung.")}
          </p>
        </>
      )}
    </div>
  );
};
